import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Settings as SettingsIcon, Link2, Key, CheckCircle, AlertTriangle,
  Unlink, Shield, Monitor, Wifi, WifiOff, RefreshCw, Download, Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import ax from '@/lib/apiClient';
import AdminSections from '@/pages/AdminSections';
import { MyProfileCard } from '@/components/MyProfileCard';
import '@/App.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LinkVenueEditor({ programId, venues, assigned, onSaved }) {
  const [sel, setSel] = useState(assigned);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSel(assigned); }, [assigned]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await ax.put(`${API}/program/links/${programId}/venues`, { venue_ids: sel });
      toast.success(data.all_venues ? 'This PC now receives all venues' : `Assigned ${data.assigned_venue_ids.length} venue(s) to this PC`);
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save assignment'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 pt-3 border-t border-[rgba(251,221,104,0.08)]">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#8892b0]">
          Venues this PC receives:{' '}
          <span className="text-white font-medium">
            {assigned.length === 0 ? 'All venues' : `${assigned.length} of ${venues.length}`}
          </span>
        </p>
        <button data-testid={`edit-venues-${programId}`} onClick={() => setOpen(!open)}
          className="text-xs font-bold text-[#fbdd68] hover:underline">{open ? 'Close' : 'Edit'}</button>
      </div>
      {open && (
        <div className="mt-3">
          <p className="text-xs text-[#8892b0] mb-2">Leave every box unchecked to send ALL venues (including new ones automatically).</p>
          <div className="grid gap-1.5 sm:grid-cols-2 max-h-48 overflow-y-auto pr-1">
            {venues.map((v) => (
              <label key={v.id} data-testid={`venue-check-${programId}-${v.id}`}
                className="flex items-center gap-2 text-sm text-white cursor-pointer rounded-lg px-2 py-1.5 hover:bg-[#fbdd68]/5">
                <input type="checkbox" checked={sel.includes(v.id)} onChange={() => toggle(v.id)}
                  className="accent-[#fbdd68]" />
                <span className="truncate">{v.name}</span>
              </label>
            ))}
            {venues.length === 0 && <p className="text-xs text-[#8892b0] italic">No venues on this account yet.</p>}
          </div>
          <Button data-testid={`save-venues-${programId}`} onClick={save} disabled={busy}
            className="mt-3 bg-[#fbdd68] text-[#000e2a] font-bold hover:bg-[#ffe685] h-8 px-4 text-xs">
            {busy ? 'Saving...' : sel.length === 0 ? 'Save (All venues)' : `Save (${sel.length} venue${sel.length !== 1 ? 's' : ''})`}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [linkStatus, setLinkStatus] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [syncBusy, setSyncBusy] = useState(false);
  const [importResult, setImportResult] = useState('');
  const [licenseKeys, setLicenseKeys] = useState([]);
  const [newKey, setNewKey] = useState('');
  const [venues, setVenues] = useState([]);
  const [linkAssignments, setLinkAssignments] = useState({});

  const fetchAssignments = async () => {
    try {
      const [{ data: linkData }, { data: venueData }] = await Promise.all([
        ax.get(`${API}/program/links`), ax.get(`${API}/schedule/venues`),
      ]);
      const map = {};
      (linkData.links || []).forEach((l) => { map[l.link_id] = l.assigned_venue_ids || []; });
      setLinkAssignments(map);
      setVenues(venueData || []);
    } catch {}
  };

  useEffect(() => { fetchLinkStatus(); }, []);

  const fetchLinkStatus = async () => {
    try {
      const { data } = await ax.get(`${API}/account/programs`);
      const active = (data.programs || []).filter(p => p.status === 'active');
      setLinkStatus({ verified: active.length > 0, programs: active });
    } catch {} finally { setFetching(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error('Code must be 6 characters'); return; }
    setLoading(true);
    try {
      // Owners and admins link via the secure path that checks the announced license key.
      // Hosts use verify-code to bind themselves to an already-linked program's account.
      const endpoint = (isAdmin || isOwner) ? `${API}/account/programs/link` : `${API}/account/verify-code`;
      const { data } = await ax.post(endpoint, { code });
      toast.success(data.message || 'Program linked');
      setCode('');
      await fetchLinkStatus();
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link program');
    } finally { setLoading(false); }
  };

  const handleUnlink = async (programId) => {
    try {
      await ax.delete(`${API}/account/programs/${programId}`);
      toast.success('Program unlinked');
      fetchLinkStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlink');
    }
  };

  const isVerified = linkStatus?.verified;
  const programs = linkStatus?.programs || [];
  const isAdmin = user?.role === 'master_admin';
  const isOwner = user?.role === 'account_owner';

  const fetchLicenseKeys = async () => {
    try {
      const { data } = await ax.get(`${API}/account/license-keys`);
      setLicenseKeys(data.keys || []);
    } catch (err) { /* account may not exist yet */ }
  };
  useEffect(() => { if (user?.role === 'account_owner' || user?.role === 'master_admin') { fetchLicenseKeys(); fetchAssignments(); } }, [user]);

  const handleAddKey = async () => {
    setSyncBusy(true);
    try {
      await ax.post(`${API}/account/license-keys`, { account_id: user?.account_id || '', license_key: newKey });
      toast.success('License key added');
      setNewKey('');
      fetchLicenseKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add that key');
    } finally { setSyncBusy(false); }
  };

  const handleExport = async () => {
    setSyncBusy(true);
    try {
      const { data } = await ax.get(`${API}/sync/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `bighat-cloud-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('Export downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally { setSyncBusy(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setSyncBusy(true);
    try {
      const body = JSON.parse(await file.text());
      const { data } = await ax.post(`${API}/sync/import`, body);
      const parts = Object.entries(data.results || {}).map(
        ([k, v]) => `${k}: +${v.added} added, ${v.updated} updated, ${v.unchanged} unchanged${v.removed ? `, ${v.removed} removed` : ''}`
      );
      setImportResult(parts.join('  |  ') + (data.errors?.length ? `  |  ${data.errors.length} item(s) skipped` : ''));
      toast.success('Import complete');
    } catch (err) {
      toast.error(err?.response?.data?.detail || (err instanceof SyntaxError ? 'That file is not valid JSON' : 'Import failed'));
    } finally { setSyncBusy(false); }
  };

  return (
    <div data-testid="settings-page">
      <div className="mb-8">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#fbdd68]/80 mb-1.5">Configuration</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-[#fbdd68]/10 border border-[#fbdd68]/20 flex items-center justify-center">
            <SettingsIcon className="w-6 h-6 text-[#fbdd68]" />
          </span>
          Settings
        </h1>
        <p className="text-sm text-[#8892b0] mt-2">
          {isAdmin ? 'Accounts and audit log' : 'Connect your BIG Hat program to the Command Center'}
        </p>
      </div>

      {isAdmin && <AdminSections />}

      {/* My Profile — every signed-in person manages their own details and host images */}
      <div className={isAdmin ? 'mt-8 mb-6' : 'mb-6'}>
        <MyProfileCard />
      </div>

      {/* Program linking — visible to ALL roles (admin, owner, host) */}
      <div className="">
      {/* Connection Status Banner */}
      <div className={`rounded-xl p-4 mb-6 flex items-center gap-3 border ${
        isVerified
          ? 'bg-[#22c55e]/10 border-[#22c55e]/30'
          : 'bg-[#fbdd68]/10 border-[#fbdd68]/30'
      }`}>
        {isVerified ? (
          <>
            <Wifi className="w-5 h-5 text-[#22c55e] shrink-0" />
            <div>
              <p className="text-sm font-semibold text-[#22c55e]">Connected</p>
              <p className="text-xs text-[#8892b0]">
                Your Command Center is linked to {programs.length} program{programs.length !== 1 ? 's' : ''}
              </p>
            </div>
          </>
        ) : (
          <>
            <WifiOff className="w-5 h-5 text-[#fbdd68] shrink-0" />
            <div>
              <p className="text-sm font-semibold text-[#fbdd68]">Not Connected</p>
              <p className="text-xs text-[#8892b0]">
                Enter the 6-character code shown in your BIG Hat program
              </p>
            </div>
          </>
        )}
      </div>

      {/* Program Code Entry */}
      <div className="glass-panel rounded-2xl p-6 sm:p-8 mb-6" data-testid="program-code-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#fbdd68]/15 flex items-center justify-center">
            <Key className="w-5 h-5 text-[#fbdd68]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Program Code</h2>
            <p className="text-xs text-[#8892b0]">
              {isOwner
                ? 'Link your BIG Hat Entertainment desktop program'
                : 'Only the account owner can link a program. Ask your owner if something looks wrong.'}
            </p>
          </div>
        </div>

        <form onSubmit={handleVerify} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <input
              data-testid="settings-program-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^ABCDEFGHJKMNPQRSTUVWXYZ23456789]/g, '').slice(0, 6))}
              placeholder="Enter 6-character code"
              maxLength={6}
              className="w-full px-4 py-3 rounded-lg text-lg text-white font-mono tracking-[0.3em] text-center uppercase outline-none transition-all duration-300"
              style={{
                backgroundColor: 'rgba(20, 27, 80, 0.6)',
                border: '1px solid rgba(251, 221, 104, 0.2)',
                letterSpacing: '0.3em',
              }}
            />
            {code.length > 0 && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8892b0]">
                {code.length}/6
              </span>
            )}
          </div>
          <button
            data-testid="settings-verify-code-button"
            type="submit"
            disabled={loading || code.length !== 6}
            className="px-6 py-3 rounded-lg font-bold text-sm transition-all duration-300 hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2 shrink-0"
            style={{ backgroundColor: '#fbdd68', color: '#000e2a', boxShadow: '0 0 15px rgba(251, 221, 104, 0.2)' }}
          >
            <Link2 className="w-4 h-4" />
            {loading ? 'Linking...' : isVerified ? 'Link Another' : 'Connect Program'}
          </button>
        </form>

        <div className="mt-3 flex items-start gap-2">
          <Monitor className="w-3.5 h-3.5 text-[#8892b0] mt-0.5 shrink-0" />
          <p className="text-xs text-[#8892b0]">
            Open your BIG Hat Entertainment program on your computer. The 6-character code is displayed on the main screen.
            Your program must be signed in with your license key and online. The code alone is not enough to link it.
          </p>
        </div>
      </div>

      {/* Linked Programs */}
      {programs.length > 0 && (
        <div className="glass-panel rounded-2xl p-6 sm:p-8 mb-6" data-testid="linked-programs-section">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#fbdd68]" />
            Linked Programs
          </h2>
          <div className="space-y-3">
            {programs.map((prog) => (
              <div
                key={prog.program_id}
                data-testid={`linked-program-${prog.program_id}`}
                className="p-4 rounded-xl border border-[rgba(251,221,104,0.1)]"
                style={{ backgroundColor: 'rgba(20, 27, 80, 0.4)' }}
              >
                <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#22c55e]/15 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-[#22c55e]" />
                  </div>
                  <div>
                    <p className="font-mono text-white text-sm tracking-widest">{prog.name || prog.code}</p>
                    <p className="text-xs text-[#8892b0]">
                      {prog.code} · Linked {prog.linked_at ? new Date(prog.linked_at).toLocaleDateString() : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#22c55e]/10 text-[#22c55e] border-[#22c55e]/30 text-xs">Active</Badge>
                  {(isOwner || isAdmin) && (
                    <button
                      data-testid={`unlink-program-${prog.program_id}`}
                      onClick={() => handleUnlink(prog.program_id)}
                      className="p-1.5 rounded-lg text-[#ef4444] hover:bg-[#ef4444]/10 transition-colors"
                    >
                      <Unlink className="w-4 h-4" />
                    </button>
                  )}
                </div>
                </div>
                {(isOwner || isAdmin) && (
                  <LinkVenueEditor programId={prog.program_id} venues={venues}
                    assigned={linkAssignments[prog.program_id] || []} onSaved={fetchAssignments} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My License Keys */}
      {(isOwner || isAdmin) && (
        <div className="glass-panel rounded-2xl p-6 sm:p-8 mb-6" data-testid="license-keys-section">
          <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
            <Key className="w-5 h-5 text-[#fbdd68]" /> My License Keys
          </h2>
          <p className="text-sm text-[#8892b0] mb-4">
            Every BIG Hat program license you own. Programs signed in with any of these keys can be linked
            to this account, and all your hosts share one schedule across them.
          </p>
          <div className="space-y-2 mb-4">
            {licenseKeys.map((k, i) => (
              <div key={i} className="flex items-center justify-between bg-[#000e2a]/60 border border-[rgba(251,221,104,0.15)] rounded-lg px-4 py-2" data-testid={`license-key-row-${i}`}>
                <span className="font-mono text-sm text-white">{k.masked}</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${k.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : k.status === 'unknown' ? 'bg-slate-500/20 text-slate-400' : 'bg-red-500/20 text-red-400'}`}>{k.status}</span>
              </div>
            ))}
            {licenseKeys.length === 0 && <p className="text-sm text-[#8892b0] italic">No license keys on this account yet.</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Input data-testid="add-license-key-input" placeholder="BHE-XXXX-XXXX-XXXX-XXXX" value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              className="bg-[#000e2a]/80 text-white placeholder-[#8892b0] border-[rgba(251,221,104,0.2)] font-mono max-w-xs" />
            <Button data-testid="add-license-key-btn" onClick={handleAddKey} disabled={syncBusy || !newKey.trim()}
              className="bg-[#fbdd68] text-[#000e2a] font-bold hover:bg-[#ffe685]">Add Key</Button>
          </div>
        </div>
      )}

      {/* Program Sync: export / import */}
      {(isOwner || isAdmin) && (
        <div className="glass-panel rounded-2xl p-6 sm:p-8 mb-6" data-testid="sync-section">
          <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-[#fbdd68]" /> Schedule Sync
          </h2>
          <p className="text-sm text-[#8892b0] mb-4">
            Your linked BIG Hat program pushes employees, venues, pricing, roles and events here on its own.
            You can also download a JSON backup of everything, or import one.
          </p>
          <div className="flex flex-wrap gap-3 items-center">
            <button data-testid="sync-export-btn" onClick={handleExport} disabled={syncBusy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#fbdd68] text-[#000e2a] text-sm font-bold hover:bg-[#ffe685] disabled:opacity-50">
              <Download className="w-4 h-4" /> Export JSON
            </button>
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[rgba(251,221,104,0.3)] text-[#fbdd68] text-sm font-bold hover:bg-[#fbdd68]/10 cursor-pointer">
              <Upload className="w-4 h-4" /> Import JSON
              <input data-testid="sync-import-input" type="file" accept=".json,application/json" className="hidden"
                onChange={handleImport} disabled={syncBusy} />
            </label>
          </div>
          {importResult && (
            <p data-testid="sync-import-result" className="text-xs text-[#8892b0] mt-3 font-mono">{importResult}</p>
          )}
        </div>
      )}

      {/* How It Works */}
      <div className="glass-panel rounded-2xl p-6 sm:p-8" data-testid="how-it-works">
        <h2 className="text-lg font-semibold text-white mb-4">How It Works</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { step: '1', title: 'Get Your Code', desc: 'Open BIG Hat Entertainment on your desktop. Find the 6-character code on the main screen.' },
            { step: '2', title: 'Enter It Here', desc: 'Type the code above. We check it against the license key your program is using, so only your own program can link.' },
            { step: '3', title: 'It Connects Itself', desc: 'Your program picks up its secure key on its own. You never copy or paste a key.' },
          ].map((item) => (
            <div key={item.step} className="p-4 rounded-xl border border-[rgba(251,221,104,0.08)]" style={{ backgroundColor: 'rgba(20, 27, 80, 0.3)' }}>
              <div className="w-7 h-7 rounded-full bg-[#fbdd68]/15 flex items-center justify-center mb-2">
                <span className="text-xs font-bold text-[#fbdd68]">{item.step}</span>
              </div>
              <h3 className="text-sm font-semibold text-white mb-1">{item.title}</h3>
              <p className="text-xs text-[#8892b0] leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

        </div>
    </div>
  );
}
